









































































































import Vue from 'vue';
import { DocumentImage, MediaItem, Nullable } from '@/types';

function getSortStatus(item: Partial<DocumentImage>): number {
  if (!item.media!.saved) return 1;

  if (item.serverStatus === 'available') return 4;
  if (item.serverStatus === 'unknown') return 3;
  if (item.serverStatus === 'missing') return 2;

  return 0;
}

export default Vue.extend({
  components: {},

  data() {
    return {
      current: null as Nullable<MediaItem>,
      currentIndex: 0,
      showFullscreen: false,
      showDelete: false,
      selected: [] as MediaItem[],
      cachedItems: [] as string[],
    };
  },

  computed: {
    items(): MediaItem[] {
      return this.$store.state.library.items.slice().reverse();
    },

    itemsWithStatus(): Partial<DocumentImage>[] {
      return this.items
        .map(item => ({
          media: item,
          url: item.url,
          serverStatus: this.$store.getters['library/serverStatus'](item.url),
          isSyncing: this.$store.getters['library/isItemSyncing'](item.url),
        }))
        .sort((a, b) => {
          return getSortStatus(a) - getSortStatus(b);
        });
    },

    unsyncedOrMissing(): Partial<DocumentImage>[] {
      return this.itemsWithStatus.filter(
        item => item.serverStatus === 'missing' || !item.media!.saved,
      );
    },

    missing(): Partial<DocumentImage>[] {
      return this.itemsWithStatus.filter(item => item.serverStatus === 'missing');
    },

    pending(): MediaItem[] {
      return this.items.filter(i => !i.saved);
    },

    syncing(): boolean {
      return this.$store.state.library.isSyncing;
    },
  },

  mounted() {
    this.verifyImages();
    this.getCachedItems();
  },

  methods: {
    async getCachedItems() {
      this.cachedItems = await this.$store.dispatch('library/getAllImagesFromCache');
    },

    statusColor(item: DocumentImage) {
      if (!item.media!.saved) return 'error';

      switch (item.serverStatus) {
        case 'available':
          return 'success';
        case 'missing':
          return 'error';
        default:
          return 'info';
      }
    },

    serverStatus(item: DocumentImage) {
      if (!item.media!.saved) return 'mdi-cloud-alert';

      switch (item.serverStatus) {
        case 'available':
          return 'mdi-cloud-check';
        case 'missing':
          return 'mdi-cloud-alert';
        default:
          return 'mdi-cloud-question';
      }
    },

    async getMissingImagesFromCache() {
      if (!this.cachedItems.length) return;

      await this.$store.dispatch('library/createAllMissingImagesFromCache');
      await this.$store.dispatch('library/verifyImages', this.cachedItems);

      await this.getCachedItems();
    },

    async verifyImages() {
      const items = this.items.filter(item => !!item.saved);

      await this.$store.dispatch(
        'library/verifyImages',
        items.map(item => item.url),
      );
    },

    addItems(files: FileList) {
      for (let i = 0; i < files.length; i += 1) {
        this.$store.dispatch('library/addFile', files[i]);
      }
    },

    selectItem(index: number, item: MediaItem) {
      if (this.selected.length) {
        this.selectItems(item);
        return;
      }

      this.currentIndex = index;
      this.current = item;
      this.showFullscreen = true;
    },

    next() {
      if (this.currentIndex + 1 < this.items.length) {
        this.currentIndex += 1;
        this.current = this.items[this.currentIndex];
      }
    },

    askRemove() {
      if (this.selected.length) {
        this.showDelete = true;
      }
    },

    async sync() {
      if (this.syncing) return;

      const unsyncedAndMissing = this.itemsWithStatus.filter(
        item => item.serverStatus === 'missing' || !item.media!.saved,
      );

      await this.$store.dispatch(
        'library/sync',
        unsyncedAndMissing.map(item => item.media),
      );

      await this.verifyImages();
    },

    async remove() {
      await this.$store.dispatch('library/remove', this.selected);

      this.selected = [];
      this.showDelete = false;
    },

    previous() {
      if (this.currentIndex > 0) {
        this.currentIndex -= 1;
        this.current = this.items[this.currentIndex];
      }
    },

    selectItems(item: MediaItem) {
      if (this.selected.includes(item)) {
        this.selected.splice(this.selected.indexOf(item), 1);
      } else {
        this.selected.push(item);
      }
    },

    selectAll() {
      this.selected = this.items.slice();
    },
  },
});
